import { useEffect, useRef } from 'react';

// Prefer absolute API base when provided; otherwise use relative '/api' paths.
const API_BASE = process.env.REACT_APP_API_BASE?.replace(/\/$/, '');

function apiUrl(path) {
    const p = path.startsWith('/') ? path : `/${path}`;
    return API_BASE ? `${API_BASE}${p}` : p; // allow proxying '/api/*' in env
}

// Safely read/remove auth items like the Client Panel does
function getToken() {
    try { return localStorage.getItem('authToken'); } catch (_) { return null; }
}
function clearAuthStorage() {
    try { localStorage.removeItem('authToken'); } catch (_) {}
    try { localStorage.removeItem('authUser'); } catch (_) {}
}

function notifyAuthChanged() {
    try {
        window.dispatchEvent(new Event('auth-changed'));
    } catch (_) {}
}

/**
 * Ends the current session (Bearer and/or cp_jwt cookie) and clears local auth state.
 */
export async function logoutSession() {
    const token = getToken();
    try {
        await fetch(apiUrl('/profile/logout'), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
        });
    } catch (_) {
        // Still clear local state if the network call fails.
    }
    clearAuthStorage();
    notifyAuthChanged();
    return true;
}

function isReactSnapRun() {
    if (typeof window === 'undefined') return false;
    return Boolean(window.__REACT_SNAP__);
}

// Coalesce concurrent checkSession calls to a single network request
let _inflightStatusPromise = null;

/**
 * Checks current auth by validating JWT against /api/profile/me.
 * Returns true if token valid; clears storage and returns false otherwise.
 */
export async function checkSession() {
    if (isReactSnapRun()) return false;
    if (_inflightStatusPromise) {
        try { return await _inflightStatusPromise; } catch { return false; }
    }
    const token = getToken();
    const perform = async () => {
        const response = await fetch(apiUrl('/auth/status'), {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            credentials: 'include',
        });

        if (response.status === 401) {
            clearAuthStorage();
            return false;
        }
        if (!response.ok) return false;

        try {
            const data = await response.json();
            if (data && data.logged_in === true) {
                if (data.user) {
                    try { localStorage.setItem('authUser', JSON.stringify(data.user)); } catch (_) {}
                }
                return true;
            }
            if (data && data.logged_in === false) {
                clearAuthStorage();
                return false;
            }
        } catch (_) {}
        return false;
    };
    try {
        _inflightStatusPromise = perform();
        const res = await _inflightStatusPromise;
        return res;
    } catch (_) {
        return false;
    } finally {
        _inflightStatusPromise = null;
    }
}

/**
 * Starts background session check + idle monitor for landing.
 * Calls onInvalidSession() when session becomes invalid or user idle > 60m.
 */
export function startSessionCheckTask(onInvalidSession) {
    if (typeof window === 'undefined') return () => {};

    let userLastActivity = Date.now();
    const resetUserActivity = () => { userLastActivity = Date.now(); };

    window.addEventListener('mousemove', resetUserActivity);
    window.addEventListener('keydown', resetUserActivity);
    window.addEventListener('click', resetUserActivity);
    window.addEventListener('scroll', resetUserActivity);

    const sessionCheckInterval = setInterval(async () => {
        const isValid = await checkSession();
        if (!isValid) {
            if (typeof onInvalidSession === 'function') onInvalidSession();
        }
    }, 5 * 60 * 1000); // every 5 minutes

    const idleCheckInterval = setInterval(() => {
        const idleTime = Date.now() - userLastActivity;
        if (idleTime >= 60 * 60 * 1000) {
            if (typeof onInvalidSession === 'function') onInvalidSession();
        }
    }, 60 * 1000); // check idle every minute

    return () => {
        clearInterval(sessionCheckInterval);
        clearInterval(idleCheckInterval);
        window.removeEventListener('mousemove', resetUserActivity);
        window.removeEventListener('keydown', resetUserActivity);
        window.removeEventListener('click', resetUserActivity);
        window.removeEventListener('scroll', resetUserActivity);
    };
}

/**
 * React hook that runs an initial check and starts background checks when valid.
 */
export function useAuthTasks(onInvalidSession) {
    const taskRef = useRef(null);

    useEffect(() => {
        if (isReactSnapRun()) return;
        const init = async () => {
            const isValid = await checkSession();
            if (isValid && !taskRef.current) {
                taskRef.current = startSessionCheckTask(onInvalidSession);
            }
        };
        init();
        return () => {
            if (taskRef.current) {
                taskRef.current();
                taskRef.current = null;
            }
        };
    }, [onInvalidSession]);
}